import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { ViewportService } from '../viewport/viewport.service';

export const FULLSCREEN_DIALOG_PANEL_CLASS = 'dialog-fullscreen';

/**
 * Opens MatDialogs that take the full screen on mobile and keep their configured size otherwise.
 * The fullscreen layout is purely the panel class (see _material.scss), so it can follow a device rotation
 * while the dialog is open. Confirmations (ConfirmationService) and the phone-sized event app preview stay plain MatDialogs.
 */
@Injectable({
  providedIn: 'root',
})
export class ResponsiveDialogService {
  private readonly _dialog = inject(MatDialog);
  private readonly _viewport = inject(ViewportService);

  open<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config: MatDialogConfig<D> = {}
  ): MatDialogRef<T, R> {
    const dialogRef = this._dialog.open<T, D, R>(component, config);

    this._viewport.isMobile$
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(isMobile =>
        isMobile
          ? dialogRef.addPanelClass(FULLSCREEN_DIALOG_PANEL_CLASS)
          : dialogRef.removePanelClass(FULLSCREEN_DIALOG_PANEL_CLASS)
      );

    return dialogRef;
  }

  closeAll(): void {
    this._dialog.closeAll();
  }
}
